import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
  Cell, CartesianGrid
} from 'recharts';
import './FMRPDatabase.css';
import { PRESET_HEADERS, PRESET_ROWS, NBSEQ_SHEETS } from './database-data';

// Images
import DB_Overview from './Database/logo/DB_Overview.jpg';
import logoBabySeq from './Database/logo/BabySeq.jpg';
import logoBabyDetect from './Database/logo/BabyDetect.png';
import logoBabyScreen from './Database/logo/BabyScreen+.jpg';
import logoEarlyCheck from './Database/logo/EarlyCheck.png';
import logoGuardian from './Database/logo/Guardian.png';
import logoACMG from './Database/logo/ACMG.jpg';
import logoNBScreening from './Database/logo/NBScreening.png';
import logoGenomics101 from './Database/logo/Genomics101.png';

const PROJECT_LOGOS = {
  BabySeq: logoBabySeq,
  NBScreening: logoNBScreening,
  BabyDetect: logoBabyDetect,
  'BabyScreen+': logoBabyScreen,
  EarlyCheck: logoEarlyCheck,
  GUARDIAN: logoGuardian,
  ACMG_Carrier: logoACMG,
  'ACMGv3.3': logoACMG,
  Genomics101: logoGenomics101,
};

const FEATURED_PROJECTS = [
  {
    id: 'BabySeq', name: 'BabySeq',
    dataIds: ['BabySeq_GroupA', 'BabySeq_GroupB'],
    description: 'The BabySeq Project is a pilot randomized clinical trial that explores the medical, behavioral, and economic impacts of nGS in well newborns and those admitted to a neonatal intensive care unit (NICU).',
    website: 'https://www.babyseqproject.org/',
    publication: 'https://doi.org/10.1016/j.ajhg.2018.11.016',
    criteria: '1. Variants classified as disease-causing in public databases with minor allele frequency < 5.0% in gnomAD.\n2. Predicted loss-of-function variants (nonsense, frameshift, ±1/2 splice-site) in disease-associated genes with minor allele frequency ≤ 0.1% in gnomAD.',
    returnedResults: '1. Monogenic disease risk: (likely) pathogenic variants in genes associated with dominantly inherited diseases or as bi-allelic (likely) pathogenic variants in genes associated with recessively inherited diseases) that present or are manageable during childhood.\n2. Monogenic disease carrier risk: the carrier status for any gene meeting the monogenic disease risk reporting criteria.\n3. PGx-associated genes (DPYD, TPMT, G6PD) that are related to atypical reactions to medications used in the pediatric population.',
  },
  {
    id: 'NBScreening', name: 'NBScreening',
    dataIds: ['NBScreening'],
    description: 'NBScreening is one of the subprojects of the Anatolian Precision Medicine Initiative (APMI). It was initiated in 2023 and represents the first NBSeq project in Türkiye.',
    website: 'https://mphenome.org/APMI',
    publication: '',
    criteria: '1. ClinVar (likely) pathogenic and ACMG (likely) pathogenic variants.\n2. Common variants (population-level minor allele frequency > 5%) within Turkish population are excluded.\n3. The minor allele frequency < 1%.',
    returnedResults: '1. Monogenic disease risk: One (likely) pathogenic variant for autosomal dominant diseases. Hemizygous (likely) pathogenic variants in male neonates and homozygous (likely) pathogenic variants in female neonates for X-linked diseases.\n2. Monogenic disease carrier risk: Two or more (likely) pathogenic variants for autosomal recessive diseases.\n3. PGx-associated genes: CACNA1S, CFTR, CYP2B6, CYP2C19, CYP2C19_GKB, CYP2C9, CYP2D6, DPYD, G6PD, HLA-A, HLA-B, IFNL3, MT-RNR1, NAT2, NUDT15, RYR1, SLCO1B1, TPMT, UGT1A1, VKORC1.',
  },
  {
    id: 'BabyDetect', name: 'BabyDetect',
    dataIds: ['BabyDetect'],
    description: 'The BabyDetect Project is designed to enroll babies born in the maternities of La Citadelle or Notre-Dame des Bruyères until summer 2025.',
    website: 'https://babydetect.com/en/',
    publication: 'https://doi.org/10.1038/s41591-024-03465-x',
    criteria: '1. ACMG class 4 (likely pathogenic) or class 5 (pathogenic) variants.\n2. ClinVar variants and the ones included in their own curated list. ClinVar variants are subsequently reviewed with particular caution using the Franklin and VarSome platforms.\n3. The minor allele frequency threshold for defining rare variants is not available.',
    returnedResults: 'Monogenic disease risk: One (likely) pathogenic variant for autosomal dominant diseases. Two (likely) pathogenic variants for autosomal recessive diseases. Hemizygous (likely) pathogenic variants in male neonates and homozygous or possible compound heterozygous (likely) pathogenic variants in female neonates for X-linked diseases.',
  },
  {
    id: 'BabyScreen+', name: 'BabyScreen+',
    dataIds: ['BabyScreen+'],
    description: 'BabyScreen+ is a research study where parents can choose to have their baby\'s heel prick sample screened for over 500 additional treatable, childhood-onset conditions. This study is investigating the best way to deliver genomic newborn screening for babies across Victoria.',
    website: 'https://babyscreen.mcri.edu.au/',
    publication: 'https://doi.org/10.1038/s41591-025-03986-z',
    criteria: '1. (Likely) pathogenic variants.\n2. For known pathogenic variants, minor allele frequency (MAF) < 2% in gnomAD; for high impact variant, MAF < 0.01% for monoallelic (dominant) disease or MAF < 0.1% for biallelic (recessive) disease.',
    returnedResults: 'Only Monogenic disease risk will be reported. Carrier status and adult-onset conditions will not be reported.',
  },
  {
    id: 'EarlyCheck', name: 'EarlyCheck',
    dataIds: ['EarlyCheck_Group1', 'EarlyCheck_Group2', 'EarlyCheck_Group3', 'EarlyCheck_Group4'],
    description: 'The NC NEXUS study seeks to assess the technical possibilities and limitations of NGS-NBS, devise and evaluate a framework to convey various types of genetic information, and develop best practices for incorporating NGS-NBS into clinical care. Early Check is a current research study that builds on NC NEXUS, open to newborns in North Carolina.',
    website: 'https://earlycheck.org',
    publication: 'https://doi.org/10.1016/j.ajhg.2020.08.001',
    criteria: 'Criteria are not publicly available.',
    returnedResults: 'Monogenic disease risk: heterozygous (likely) pathogenic variants in a gene associated with a dominant condition; two or more (likely) pathogenic variants in a gene associated with a recessive condition.',
  },
  {
    id: 'GUARDIAN', name: 'GUARDIAN',
    dataIds: ['Guardian_Group1', 'Guardian_Group2'],
    description: 'The Genomic Uniform-screening Against Rare Disease in All Newborns (GUARDIAN) study was initiated with the aim of screening a diverse population by parent-reported race and ethnicity of newborns in New York City within the context of the New York State Department of Health Newborn Screening Program.',
    website: 'https://guardian-study.org/',
    publication: 'https://jamanetwork.com/journals/jama/fullarticle/2825327',
    criteria: '1. CFTR p. D1270N and p.R74W variants are excluded.\n2. The minor allele frequency threshold for defining rare variants is not available.',
    returnedResults: 'Monogenic disease risk: heterozygous (likely) pathogenic variants in a gene associated with a dominant condition. Two or more (likely) pathogenic variants, or variants of uncertain significance co-occurred with (likely) pathogenic variants in a gene associated with a recessive condition. Hemizygous (likely) pathogenic variants in male neonates and homozygous (likely) pathogenic variants in female neonates for X-linked diseases.',
  },
  {
    id: 'ACMG_Carrier', name: 'ACMG Carrier Screening',
    dataIds: ['ACMG_Carrier_Tier_1', 'ACMG_Carrier_Tier_2', 'ACMG_Carrier_Tier_3', 'ACMG_Carrier_Tier_4', 'ACMG_Carrier_Outside_gnomAD'],
    description: 'ACMG expanded carrier screening is a genetic test that checks for a wide range of inherited recessive disorders to help people make informed reproductive decisions, especially those who are pregnant or planning a pregnancy.',
    website: 'https://www.acmg.net',
    publication: 'https://doi.org/10.1038/s41436-021-01203-z',
    criteria: '',
    returnedResults: '',
  },
  {
    id: 'ACMGv3.3', name: 'ACMG v3.3',
    dataIds: ['ACMGv3.3'],
    description: 'The ACMG secondary findings list contains genes associated with serious, actionable genetic diseases. These genes are recommended for opportunistic screening during clinical exome or genome sequencing, even if the patient\'s primary reason for testing was different.',
    website: 'https://www.acmg.net/',
    publication: 'https://doi.org/10.1016/j.gim.2025.101454',
    criteria: '',
    returnedResults: '',
  },
  {
    id: 'Genomics101', name: 'Genomics101',
    dataIds: ['Genomic101'],
    description: 'Genomics101, also known as the Generation Study, is the research project carried out by Genomics England in partnership with the NHS, to conduct whole-genome sequencing on 100,000 newborns. It aims to identify treatable rare conditions in babies earlier, support research on rare genetic conditions, and explore the benefits and risks of storing a baby\'s genome throughout their lifetime.',
    website: 'https://www.genomicsengland.co.uk/blog/genomics-101-what-is-the-genome',
    publication: '',
  },
];

// Map Project column values to display names for charts
const normalizeProject = (p) => {
  if (/ACMG_Carrier/i.test(p)) return 'ACMG_Carrier';
  if (/BabySeq/i.test(p)) return 'BabySeq';
  if (/EarlyCheck/i.test(p)) return 'EarlyCheck';
  if (/Guardian/i.test(p)) return 'GUARDIAN';
  return p;
};

const ONTOLOGY_COLORS = [
  '#A8D5BA', '#F7C59F', '#C9B6E4', '#F4A7B9', '#DCEFB7',
  '#F9E79F', '#D7CCC8', '#AED6F1', '#F5B7B1',
];

const INTERSECTION_GRADIENT = (count, maxCount) => {
  const t = maxCount > 0 ? count / maxCount : 0;
  const r = Math.round(137 + (42 - 137) * t);
  const g = Math.round(194 + (111 - 194) * t);
  const b = Math.round(217 + (151 - 217) * t);
  return `rgb(${r},${g},${b})`;
};

/* ───────── helpers ───────── */

/* ───────── COMPONENT ───────── */
export default function FMRPDatabase() {
  const [tab, setTab] = useState('home');

  // Inline data — converted from TSV/XLSX at build time
  const presetData = useMemo(() => ({
    headers: PRESET_HEADERS,
    rows: PRESET_ROWS.map(r => {
      const obj = {};
      PRESET_HEADERS.forEach((h, i) => { obj[h] = r[i] || ''; });
      return obj;
    }),
  }), []);
  const nbseqSheets = NBSEQ_SHEETS;

  // Screening List Details state
  const [selectedProject, setSelectedProject] = useState(null);
  const [detailSearch, setDetailSearch] = useState('');
  const [detailPage, setDetailPage] = useState(0);

  // Screening Results state
  const [resultsSubTab, setResultsSubTab] = useState(() => {
    const sheets = Object.keys(NBSEQ_SHEETS).filter(n => !['All-Positive', 'All-Carrier', 'Positive-rates'].includes(n));
    return sheets.length > 0 ? sheets[0] : null;
  });

  // Generator state
  const [genSearch, setGenSearch] = useState('');
  const [genPage, setGenPage] = useState(0);
  const [genSelectedRows, setGenSelectedRows] = useState(new Set());
  const [genFilterOpen, setGenFilterOpen] = useState(false);
  const [genFilterProjects, setGenFilterProjects] = useState(null); // null = all
  const [genFilterInheritance, setGenFilterInheritance] = useState(null);
  const [customRows, setCustomRows] = useState([]);
  const [customInputs, setCustomInputs] = useState({});

  const PAGE_SIZE = 15;

  /* ───── CHART DATA ───── */

  // Ontology bar chart data
  const ontologyData = useMemo(() => {
    if (!presetData) return [];
    const { rows } = presetData;

    // Normalize: multi-group → "Complex genetic syndromes", normalize project names
    const entries = rows
      .filter(r => r.Genes)
      .map(r => ({
        disorderGroup: r.Disorder_Group.includes(';') ? 'Complex genetic syndromes' : r.Disorder_Group,
        project: normalizeProject(r.Project),
        cor: `${r.Genes}_${r.Disease}`,
      }));

    // Unique cor+project+disorderGroup combos
    const seen = new Set();
    const unique = entries.filter(e => {
      const key = `${e.cor}|${e.project}|${e.disorderGroup}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    // Group by disorderGroup+project → count distinct cor
    const grouped = {};
    unique.forEach(e => {
      const key = `${e.disorderGroup}|||${e.project}`;
      if (!grouped[key]) grouped[key] = new Set();
      grouped[key].add(e.cor);
    });

    // Build chart-friendly structure
    const projectSet = new Set();
    const dgMap = {};
    Object.entries(grouped).forEach(([key, corSet]) => {
      const [dg, proj] = key.split('|||');
      projectSet.add(proj);
      if (!dgMap[dg]) dgMap[dg] = { disorderGroup: dg, total: 0 };
      dgMap[dg][proj] = corSet.size;
      dgMap[dg].total += corSet.size;
    });

    const projects = [...projectSet].sort();
    const sorted = Object.values(dgMap).sort((a, b) => b.total - a.total);
    return { chartData: sorted, projects };
  }, [presetData]);

  // Intersection (UpSet-style) bar chart
  const intersectionData = useMemo(() => {
    if (!presetData) return [];
    const { rows } = presetData;

    const entries = rows
      .filter(r => r.Genes)
      .map(r => ({ cor: `${r.Genes}_${r.Disease}`, project: normalizeProject(r.Project) }));

    // cor → set of projects
    const corProjects = {};
    entries.forEach(e => {
      if (!corProjects[e.cor]) corProjects[e.cor] = new Set();
      corProjects[e.cor].add(e.project);
    });

    // Single sets
    const allProjects = [...new Set(entries.map(e => e.project))].sort();
    const counts = {};
    allProjects.forEach(p => {
      counts[p] = 0;
    });
    Object.values(corProjects).forEach(pSet => {
      pSet.forEach(p => { counts[p] = (counts[p] || 0) + 1; });
    });

    // Pairwise intersections
    for (let i = 0; i < allProjects.length - 1; i++) {
      for (let j = i + 1; j < allProjects.length; j++) {
        const p1 = allProjects[i];
        const p2 = allProjects[j];
        let count = 0;
        Object.values(corProjects).forEach(pSet => {
          if (pSet.has(p1) && pSet.has(p2)) count++;
        });
        if (count > 0) {
          counts[`${p1} ∩ ${p2}`] = count;
        }
      }
    }

    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [presetData]);

  // Positive rate data (hardcoded from R script)
  const positiveRateData = useMemo(() => [
    { project: 'BabySeq', total: 17 },
    { project: 'BabyDetect', total: 14 },
    { project: 'BabyScreen+', total: 10 },
    { project: 'GUARDIAN', total: 12 },
    { project: 'EarlyCheck', total: 7 },
  ].sort((a, b) => a.total - b.total), []);

  // Venn data (just display as text/lists since true venn diagrams are hard in React)
  const vennCarrierData = useMemo(() => {
    if (!nbseqSheets || !nbseqSheets['All-Carrier']) return null;
    const data = nbseqSheets['All-Carrier'];
    const byProject = {};
    data.forEach(r => {
      const proj = r.Project || '';
      const gene = r.Genes || r.Gene || '';
      if (!gene) return;
      if (!byProject[proj]) byProject[proj] = new Set();
      byProject[proj].add(gene);
    });
    return byProject;
  }, [nbseqSheets]);

  const vennPositiveData = useMemo(() => {
    if (!nbseqSheets || !nbseqSheets['All-Positive']) return null;
    const data = nbseqSheets['All-Positive'].filter(r => (r.Genes || r.Gene || '') !== 'BRCA2');
    const byProject = {};
    data.forEach(r => {
      const proj = r.Project || '';
      const gene = r.Genes || r.Gene || '';
      if (!gene) return;
      if (!byProject[proj]) byProject[proj] = new Set();
      byProject[proj].add(gene);
    });
    return byProject;
  }, [nbseqSheets]);

  /* ───── SCREENING LIST DETAILS ───── */

  const detailProjectDef = useMemo(() => {
    if (!selectedProject) return null;
    return FEATURED_PROJECTS.find(p => p.id === selectedProject);
  }, [selectedProject]);

  const detailRows = useMemo(() => {
    if (!presetData || !detailProjectDef) return [];
    return presetData.rows.filter(r => detailProjectDef.dataIds.includes(r.Project));
  }, [presetData, detailProjectDef]);

  const detailFiltered = useMemo(() => {
    if (!detailSearch) return detailRows;
    const q = detailSearch.toLowerCase();
    return detailRows.filter(r =>
      Object.values(r).some(v => v.toLowerCase().includes(q))
    );
  }, [detailRows, detailSearch]);

  /* ───── GENERATOR ───── */

  const allGeneratorRows = useMemo(() => {
    if (!presetData) return [];
    return [...presetData.rows, ...customRows];
  }, [presetData, customRows]);

  const allProjects = useMemo(() => {
    const s = new Set();
    allGeneratorRows.forEach(r => { if (r.Project) s.add(r.Project); });
    return [...s].sort();
  }, [allGeneratorRows]);

  const allInheritances = useMemo(() => {
    const s = new Set();
    allGeneratorRows.forEach(r => { if (r.Inheritance) s.add(r.Inheritance); });
    return [...s].sort();
  }, [allGeneratorRows]);

  const genFiltered = useMemo(() => {
    let data = allGeneratorRows;
    if (genFilterProjects) {
      data = data.filter(r => genFilterProjects.has(r.Project));
    }
    if (genFilterInheritance) {
      data = data.filter(r => genFilterInheritance.has(r.Inheritance));
    }
    if (genSearch) {
      const q = genSearch.toLowerCase();
      data = data.filter(r => Object.values(r).some(v => (v || '').toLowerCase().includes(q)));
    }
    return data;
  }, [allGeneratorRows, genFilterProjects, genFilterInheritance, genSearch]);

  // Reset selection when filters change
  useEffect(() => {
    if (genFilterProjects || genFilterInheritance) {
      setGenSelectedRows(new Set(genFiltered.map((_, i) => i)));
    }
  }, [genFilterProjects, genFilterInheritance, genFiltered]);

  const handleGenToggleRow = useCallback((idx) => {
    setGenSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  }, []);

  const handleGenSelectAll = useCallback(() => {
    setGenSelectedRows(new Set(genFiltered.map((_, i) => i)));
  }, [genFiltered]);

  const handleGenDeselectAll = useCallback(() => {
    setGenSelectedRows(new Set());
  }, []);

  const handleAddCustomRow = useCallback(() => {
    if (!presetData) return;
    const hasContent = presetData.headers.some(h => customInputs[h]?.trim());
    if (!hasContent) return;
    const newRow = {};
    presetData.headers.forEach(h => { newRow[h] = customInputs[h] || ''; });
    setCustomRows(prev => [...prev, newRow]);
    setCustomInputs({});
  }, [presetData, customInputs]);

  const handleDownloadSelected = useCallback(() => {
    if (!presetData || genSelectedRows.size === 0) return;
    const selected = genFiltered.filter((_, i) => genSelectedRows.has(i));
    const tsv = [presetData.headers.join('\t'), ...selected.map(r => presetData.headers.map(h => r[h] || '').join('\t'))].join('\n');
    const blob = new Blob([tsv], { type: 'text/tab-separated-values' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `screening_list_selected_${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }, [presetData, genFiltered, genSelectedRows]);

  const handleDownloadAll = useCallback(() => {
    if (!presetData) return;
    const data = genFiltered;
    const tsv = [presetData.headers.join('\t'), ...data.map(r => presetData.headers.map(h => r[h] || '').join('\t'))].join('\n');
    const blob = new Blob([tsv], { type: 'text/tab-separated-values' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `screening_list_all_${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }, [presetData, genFiltered]);

  const handleApplyFilters = useCallback(() => {
    // Read checked states from DOM
    const projChecked = document.querySelectorAll('.fmrpdb-filter-proj:checked');
    const inhChecked = document.querySelectorAll('.fmrpdb-filter-inh:checked');
    setGenFilterProjects(projChecked.length === allProjects.length ? null : new Set([...projChecked].map(c => c.value)));
    setGenFilterInheritance(inhChecked.length === allInheritances.length ? null : new Set([...inhChecked].map(c => c.value)));
    setGenPage(0);
  }, [allProjects, allInheritances]);

  const handleResetFilters = useCallback(() => {
    setGenFilterProjects(null);
    setGenFilterInheritance(null);
    setGenSearch('');
    setGenSelectedRows(new Set());
    setGenPage(0);
  }, []);

  /* ───── Venn helper ───── */
  const computeVennSummary = (byProject) => {
    if (!byProject) return null;
    const projects = Object.keys(byProject);
    const allGenes = new Set();
    projects.forEach(p => byProject[p].forEach(g => allGenes.add(g)));

    // per-project counts
    const counts = {};
    projects.forEach(p => { counts[p] = byProject[p].size; });

    // pairwise overlaps
    const overlaps = [];
    for (let i = 0; i < projects.length - 1; i++) {
      for (let j = i + 1; j < projects.length; j++) {
        const shared = [...byProject[projects[i]]].filter(g => byProject[projects[j]].has(g));
        if (shared.length > 0) {
          overlaps.push({ p1: projects[i], p2: projects[j], count: shared.length, genes: shared });
        }
      }
    }
    return { projects, counts, overlaps, total: allGenes.size };
  };

  /* ───── Paginated table helper ───── */
  const renderPaginatedTable = (headers, rows, page, setPage, search, setSearch, pageSize = PAGE_SIZE, options = {}) => {
    const { selectable, selectedSet, onToggle, onSelectAll, onDeselectAll } = options;
    const start = page * pageSize;
    const pageRows = rows.slice(start, start + pageSize);
    const totalPages = Math.ceil(rows.length / pageSize);

    return (
      <div className="fmrpdb-table-wrap">
        <div className="fmrpdb-table-toolbar">
          <div className="fmrpdb-table-search">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
            />
          </div>
          <span className="fmrpdb-table-info">{rows.length} entries</span>
          {selectable && (
            <div className="fmrpdb-table-sel-actions">
              <button onClick={onSelectAll} className="fmrpdb-btn-sm">Select All</button>
              <button onClick={onDeselectAll} className="fmrpdb-btn-sm">Deselect All</button>
              <span className="fmrpdb-sel-count">{selectedSet?.size || 0} selected</span>
            </div>
          )}
        </div>
        <div className="fmrpdb-table-scroll">
          <table className="fmrpdb-table">
            <thead>
              <tr>
                {selectable && <th className="fmrpdb-th-check"></th>}
                {headers.map(h => <th key={h}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr><td colSpan={headers.length + (selectable ? 1 : 0)} className="fmrpdb-empty">No data</td></tr>
              ) : pageRows.map((row, i) => {
                const globalIdx = start + i;
                return (
                  <tr key={globalIdx} className={selectable && selectedSet?.has(globalIdx) ? 'selected' : ''}>
                    {selectable && (
                      <td className="fmrpdb-td-check">
                        <input
                          type="checkbox"
                          checked={selectedSet?.has(globalIdx) || false}
                          onChange={() => onToggle(globalIdx)}
                        />
                      </td>
                    )}
                    {headers.map(h => <td key={h}>{row[h] || ''}</td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="fmrpdb-pagination">
            <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</button>
            <span>Page {page + 1} of {totalPages}</span>
            <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        )}
      </div>
    );
  };

  /* ───── RENDER ───── */
  return (
    <div className="fmrpdb">
      {/* Sub-navigation tabs */}
      <div className="fmrpdb-tabs">
        {[
          { key: 'home', label: 'Overview', icon: 'fa-database' },
          { key: 'details', label: 'Projects', icon: 'fa-list-alt' },
          { key: 'results', label: 'Screening Results', icon: 'fa-chart-bar' },
          { key: 'generator', label: 'Generator', icon: 'fa-cogs' },
        ].map(t => (
          <button
            key={t.key}
            className={`fmrpdb-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => { setTab(t.key); if (t.key === 'details') setSelectedProject(null); }}
          >
            <i className={`fas ${t.icon}`}></i> {t.label}
          </button>
        ))}
      </div>

      <div className="fmrpdb-content">
        {/* ════════ OVERVIEW ════════ */}
        {tab === 'home' && (
          <div className="fmrpdb-home">
            <div className="fmrpdb-header">
              <h1>FamilyRisk Database</h1>
              <p>Unified knowledge base for newborn and carrier screening gene-disease associations.</p>
            </div>

            <p className="fmrpdb-intro">
              Curated gene–disease screening lists from six leading NBSeq projects and ACMG recommendations. For each gene-disease association, the corresponding validity from GenCC is provided. Browse project details under "Projects", published screening results under "Screening Results", or build a custom panel with "Generator".
            </p>

            <div className="fmrpdb-overview-img">
              <img src={DB_Overview} alt="Database Overview" />
            </div>

            {/* Ontology Chart */}
            <div className="fmrpdb-chart-section">
              <h2 className="fmrpdb-section-title">Disease Ontology</h2>
              <p className="fmrpdb-section-desc">
                3115 gene-disease associations from 8 screening lists across 13 disease ontologies. Multi-ontology diseases are classified as "Complex genetic syndromes".
              </p>
              {ontologyData.chartData && (
                <div className="fmrpdb-chart-container">
                  <ResponsiveContainer width="100%" height={Math.max(400, ontologyData.chartData.length * 40)}>
                    <BarChart
                      data={ontologyData.chartData}
                      layout="vertical"
                      margin={{ top: 10, right: 30, left: 250, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.2)" />
                      <XAxis type="number" tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} />
                      <YAxis
                        type="category"
                        dataKey="disorderGroup"
                        width={240}
                        tick={{ fill: 'var(--text-secondary)', fontSize: 11 }}
                      />
                      <Tooltip
                        contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: 8 }}
                        labelStyle={{ color: 'var(--text-primary)' }}
                      />
                      <Legend wrapperStyle={{ color: 'var(--text-primary)' }} />
                      {ontologyData.projects.map((proj, i) => (
                        <Bar key={proj} dataKey={proj} stackId="a" fill={ONTOLOGY_COLORS[i % ONTOLOGY_COLORS.length]} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Intersection Chart */}
            <div className="fmrpdb-chart-section">
              <h2 className="fmrpdb-section-title">Screening List Intersection</h2>
              <p className="fmrpdb-section-desc">
                Overlap of gene-disease associations across different screening lists and projects.
              </p>
              {intersectionData.length > 0 && (
                <div className="fmrpdb-chart-container">
                  <ResponsiveContainer width="100%" height={500}>
                    <BarChart
                      data={intersectionData}
                      margin={{ top: 10, right: 30, left: 20, bottom: 120 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.2)" />
                      <XAxis
                        dataKey="name"
                        angle={-45}
                        textAnchor="end"
                        interval={0}
                        tick={{ fill: 'var(--text-secondary)', fontSize: 10 }}
                        height={120}
                      />
                      <YAxis tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} label={{ value: 'Shared Gene-Disease Pairs', angle: -90, position: 'insideLeft', fill: 'var(--text-secondary)' }} />
                      <Tooltip
                        contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: 8 }}
                      />
                      <Bar dataKey="count">
                        {intersectionData.map((entry, i) => (
                          <Cell key={i} fill={INTERSECTION_GRADIENT(entry.count, intersectionData[0]?.count || 1)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

          </div>
        )}

        {/* ════════ SCREENING LIST DETAILS ════════ */}
        {tab === 'details' && (
          <div className="fmrpdb-details">
            {!selectedProject ? (
              <>
                <div className="fmrpdb-header">
                  <h1>Screening List Projects</h1>
                  <p>Select a project to view its screening list and details.</p>
                </div>
                <div className="fmrpdb-project-grid">
                  {FEATURED_PROJECTS.map(proj => (
                    <div key={proj.id} className="fmrpdb-project-card" onClick={() => { setSelectedProject(proj.id); setDetailSearch(''); setDetailPage(0); }}>
                      <div className="fmrpdb-project-logo">
                        {PROJECT_LOGOS[proj.id] && <img src={PROJECT_LOGOS[proj.id]} alt={proj.name} />}
                      </div>
                      <div className="fmrpdb-project-name">{proj.name}</div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="fmrpdb-detail-view">
                <div className="fmrpdb-detail-sidebar">
                  <button className="fmrpdb-back-btn" onClick={() => setSelectedProject(null)}>
                    <i className="fas fa-arrow-left"></i> Back to all projects
                  </button>
                  <h3>{detailProjectDef?.name}</h3>
                  <div className="fmrpdb-detail-info-section">
                    <h4>Project Information</h4>
                    <p>{detailProjectDef?.description}</p>
                  </div>
                  {detailProjectDef?.criteria && (
                    <div className="fmrpdb-detail-info-section">
                      <h4>Filtering Criteria</h4>
                      <p style={{ whiteSpace: 'pre-line' }}>{detailProjectDef.criteria}</p>
                    </div>
                  )}
                  {detailProjectDef?.returnedResults && (
                    <div className="fmrpdb-detail-info-section">
                      <h4>Returned Results</h4>
                      <p style={{ whiteSpace: 'pre-line' }}>{detailProjectDef.returnedResults}</p>
                    </div>
                  )}
                  <div className="fmrpdb-detail-links">
                    <h4>Quick Links</h4>
                    {detailProjectDef?.website && (
                      <a href={detailProjectDef.website} target="_blank" rel="noopener noreferrer" className="fmrpdb-link-btn">
                        <i className="fas fa-globe"></i> Website
                      </a>
                    )}
                    {detailProjectDef?.publication && (
                      <a href={detailProjectDef.publication} target="_blank" rel="noopener noreferrer" className="fmrpdb-link-btn">
                        <i className="fas fa-file-alt"></i> Publication
                      </a>
                    )}
                  </div>
                </div>
                <div className="fmrpdb-detail-main">
                  <h3>Screening List</h3>
                  {presetData && renderPaginatedTable(
                    presetData.headers,
                    detailFiltered,
                    detailPage,
                    setDetailPage,
                    detailSearch,
                    setDetailSearch,
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ════════ NBSEQ SCREENING RESULTS ════════ */}
        {tab === 'results' && (
          <div className="fmrpdb-results">
            <div className="fmrpdb-header">
              <h1>NBSeq Screening Results</h1>
              <p>Published screening results from BabySeq, BabyDetect, EarlyCheck, Guardian and BabyScreen+.</p>
            </div>
            <p className="fmrpdb-results-intro">
              Only BabySeq and EarlyCheck disclosed positive disease carriers in their publications.
            </p>
            <p className="fmrpdb-results-rates">
              Screening rates — GUARDIAN: 3.7% | BabyDetect: 1.8% | BabyScreen+: 1.6% | BabySeq: 9.4% | EarlyCheck: 3.8%
            </p>

            <div className="fmrpdb-results-charts">
              {/* Positive Rate Bar Chart */}
              <div className="fmrpdb-results-chart-card">
                <h4>Number of mutated genes of screen-positive rare diseases</h4>
                <p className="fmrpdb-chart-note">* G6PD is shared among GUARDIAN, BabyDetect, BabyScreen+. * G6PD and ACADS are shared between GUARDIAN and BabyDetect. * BRCA2 is shared between BabySeq and EarlyCheck.</p>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={positiveRateData} layout="vertical" margin={{ left: 100, right: 20, top: 10, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.2)" />
                    <XAxis type="number" tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} />
                    <YAxis type="category" dataKey="project" tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} width={95} />
                    <Tooltip contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: 8 }} />
                    <Bar dataKey="total" fill="#cdb4db" name="No. of mutated genes" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Carrier Venn */}
              <div className="fmrpdb-results-chart-card">
                <h4>Intersected mutated genes of disease carrier status</h4>
                {(() => {
                  const summary = computeVennSummary(vennCarrierData);
                  if (!summary) return <p>No data available</p>;
                  return (
                    <div className="fmrpdb-venn-summary">
                      {summary.projects.map(p => (
                        <div key={p} className="fmrpdb-venn-item">
                          <span className="fmrpdb-venn-project">{p}</span>
                          <span className="fmrpdb-venn-count">{summary.counts[p]} genes</span>
                        </div>
                      ))}
                      {summary.overlaps.length > 0 && (
                        <div className="fmrpdb-venn-overlaps">
                          <h5>Shared genes:</h5>
                          {summary.overlaps.map((o, i) => (
                            <div key={i} className="fmrpdb-venn-overlap">
                              <span>{o.p1} ∩ {o.p2}: <strong>{o.count}</strong></span>
                              <span className="fmrpdb-venn-genes">{o.genes.join(', ')}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Positive Venn */}
              <div className="fmrpdb-results-chart-card">
                <h4>Intersected mutated genes of screen-positive rare diseases</h4>
                {(() => {
                  const summary = computeVennSummary(vennPositiveData);
                  if (!summary) return <p>No data available</p>;
                  return (
                    <div className="fmrpdb-venn-summary">
                      {summary.projects.map(p => (
                        <div key={p} className="fmrpdb-venn-item">
                          <span className="fmrpdb-venn-project">{p}</span>
                          <span className="fmrpdb-venn-count">{summary.counts[p]} genes</span>
                        </div>
                      ))}
                      {summary.overlaps.length > 0 && (
                        <div className="fmrpdb-venn-overlaps">
                          <h5>Shared genes:</h5>
                          {summary.overlaps.map((o, i) => (
                            <div key={i} className="fmrpdb-venn-overlap">
                              <span>{o.p1} ∩ {o.p2}: <strong>{o.count}</strong></span>
                              <span className="fmrpdb-venn-genes">{o.genes.join(', ')}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Per-sheet result tables */}
            <h2 className="fmrpdb-section-title" style={{ marginTop: '3vh' }}>Result Details</h2>
            <p className="fmrpdb-section-desc">"Positives" = screen-positive rare diseases. "Carriers" = disease carrier-status.</p>

            {nbseqSheets && (() => {
              const tabSheets = Object.keys(nbseqSheets).filter(n => !['All-Positive', 'All-Carrier', 'Positive-rates'].includes(n));
              return (
                <>
                  <div className="fmrpdb-results-subtabs">
                    {tabSheets.map(sheet => (
                      <button
                        key={sheet}
                        className={`fmrpdb-results-subtab ${resultsSubTab === sheet ? 'active' : ''}`}
                        onClick={() => setResultsSubTab(sheet)}
                      >
                        {sheet}
                      </button>
                    ))}
                  </div>
                  {resultsSubTab && nbseqSheets[resultsSubTab] && (() => {
                    const sheetData = nbseqSheets[resultsSubTab];
                    const headers = sheetData.length > 0 ? Object.keys(sheetData[0]).filter(k => sheetData.some(r => r[k] !== '')) : [];
                    return (
                      <ResultsSheetTable headers={headers} data={sheetData} />
                    );
                  })()}
                </>
              );
            })()}
          </div>
        )}

        {/* ════════ SCREENING LIST GENERATOR ════════ */}
        {tab === 'generator' && presetData && (
          <div className="fmrpdb-generator">
            <div className="fmrpdb-header">
              <h1>Screening List Generator</h1>
              <p>Filter, customize, and export screening panels for use in FamilyRisk.</p>
            </div>

            {/* Custom Row Entry */}
            <div className="fmrpdb-custom-entry">
              <h4>Quick Add Custom Entry:</h4>
              <div className="fmrpdb-custom-inputs">
                {presetData.headers.slice(0, 6).map(h => (
                  <input
                    key={h}
                    placeholder={h}
                    value={customInputs[h] || ''}
                    onChange={e => setCustomInputs(prev => ({ ...prev, [h]: e.target.value }))}
                  />
                ))}
                <button className="fmrpdb-btn-add" onClick={handleAddCustomRow}>Add Row</button>
              </div>
              {customRows.length > 0 && (
                <div className="fmrpdb-custom-info">
                  <span>{customRows.length} custom {customRows.length > 1 ? 'entries' : 'entry'} added</span>
                  <button className="fmrpdb-btn-sm fmrpdb-btn-warn" onClick={() => setCustomRows([])}>Clear</button>
                </div>
              )}
            </div>

            {/* Downloads + Filter Toggle */}
            <div className="fmrpdb-gen-toolbar">
              <h3>Preset Screening List:</h3>
              <div className="fmrpdb-gen-actions">
                <button className="fmrpdb-btn-primary" onClick={handleDownloadSelected} disabled={genSelectedRows.size === 0}>
                  <i className="fas fa-download"></i> Download Selected ({genSelectedRows.size})
                </button>
                <button className="fmrpdb-btn-secondary" onClick={handleDownloadAll}>
                  <i className="fas fa-download"></i> Download All
                </button>
              </div>
            </div>

            {/* Advanced Filters */}
            <div className="fmrpdb-filter-panel">
              <button className="fmrpdb-filter-toggle" onClick={() => setGenFilterOpen(o => !o)}>
                {genFilterOpen ? '▼' : '▶'} Advanced Filters
              </button>
              {genFilterOpen && (
                <div className="fmrpdb-filter-body">
                  <div className="fmrpdb-filter-cols">
                    <div className="fmrpdb-filter-col">
                      <h5>Project</h5>
                      <div className="fmrpdb-filter-actions">
                        <button className="fmrpdb-btn-xs" onClick={() => { document.querySelectorAll('.fmrpdb-filter-proj').forEach(c => { c.checked = true; }); }}>Select All</button>
                        <button className="fmrpdb-btn-xs" onClick={() => { document.querySelectorAll('.fmrpdb-filter-proj').forEach(c => { c.checked = false; }); }}>Deselect All</button>
                      </div>
                      <div className="fmrpdb-filter-checks">
                        {allProjects.map(p => (
                          <label key={p}><input type="checkbox" className="fmrpdb-filter-proj" value={p} defaultChecked /> {p}</label>
                        ))}
                      </div>
                    </div>
                    <div className="fmrpdb-filter-col">
                      <h5>Inheritance</h5>
                      <div className="fmrpdb-filter-actions">
                        <button className="fmrpdb-btn-xs" onClick={() => { document.querySelectorAll('.fmrpdb-filter-inh').forEach(c => { c.checked = true; }); }}>Select All</button>
                        <button className="fmrpdb-btn-xs" onClick={() => { document.querySelectorAll('.fmrpdb-filter-inh').forEach(c => { c.checked = false; }); }}>Deselect All</button>
                      </div>
                      <div className="fmrpdb-filter-checks">
                        {allInheritances.map(inh => (
                          <label key={inh}><input type="checkbox" className="fmrpdb-filter-inh" value={inh} defaultChecked /> {inh}</label>
                        ))}
                      </div>
                    </div>
                    <div className="fmrpdb-filter-col fmrpdb-filter-btns">
                      <button className="fmrpdb-btn-primary" onClick={handleApplyFilters}>Apply</button>
                      <button className="fmrpdb-btn-secondary" onClick={handleResetFilters}>Reset</button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {renderPaginatedTable(
              presetData.headers,
              genFiltered,
              genPage,
              setGenPage,
              genSearch,
              setGenSearch,
              10,
              {
                selectable: true,
                selectedSet: genSelectedRows,
                onToggle: handleGenToggleRow,
                onSelectAll: handleGenSelectAll,
                onDeselectAll: handleGenDeselectAll,
              }
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* Small sub-component for results sheet tables (has own search/page state) */
function ResultsSheetTable({ headers, data }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => { setSearch(''); setPage(0); }, [data]);

  const filtered = useMemo(() => {
    if (!search) return data;
    const q = search.toLowerCase();
    return data.filter(r => headers.some(h => String(r[h] || '').toLowerCase().includes(q)));
  }, [data, search, headers]);

  const pageSize = 10;
  const start = page * pageSize;
  const pageRows = filtered.slice(start, start + pageSize);
  const totalPages = Math.ceil(filtered.length / pageSize);

  return (
    <div className="fmrpdb-table-wrap">
      <div className="fmrpdb-table-toolbar">
        <div className="fmrpdb-table-search">
          <i className="fas fa-search"></i>
          <input type="text" placeholder="Search..." value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} />
        </div>
        <span className="fmrpdb-table-info">{filtered.length} entries</span>
      </div>
      <div className="fmrpdb-table-scroll">
        <table className="fmrpdb-table">
          <thead><tr>{headers.map(h => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr><td colSpan={headers.length} className="fmrpdb-empty">No data</td></tr>
            ) : pageRows.map((row, i) => (
              <tr key={i}>{headers.map(h => <td key={h}>{row[h] !== undefined ? String(row[h]) : ''}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="fmrpdb-pagination">
          <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</button>
          <span>Page {page + 1} of {totalPages}</span>
          <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
